/* ============================================================
   NORTHEN TRADING LABS — Homepage JavaScript
   Nav scroll state, scroll reveal, the gate-ladder animation, and the
   Trading Desk's own filter window running as a demonstration.

   Both of the last two are the REAL desk modules — NtlMarketMap for the
   table, NtlMarketFilter + NtlGatePanel for the window — fed frozen data
   and run in demo mode. This file owns no markup builders and no URLs.

   (A hand-drawn schematic — four gates, six markets, a square each — used to
   open the hero. It went when the gate table below it became legible on a
   phone: two pictures of one mechanism, and only one of them was the product.)
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Hooks the sections below hand to REEL MODE (the bottom of this file). Empty until they do.
  const reelHooks = {};

  // ============================================================
  // NAV SCROLL BEHAVIOUR
  // ============================================================
  const nav = document.getElementById('nav');
  if (nav) {
    window.addEventListener('scroll', () => {
      nav.classList.toggle('scrolled', window.scrollY > 40);
    }, { passive: true });
  }

  // Theme toggle and dropdowns are owned by the shared navbar partial
  // (_NavPublic inline script). Only the home page's own behaviour lives here.

  // ============================================================
  // INTERSECTION OBSERVER — SCROLL REVEAL
  // ============================================================
  const revealElements = document.querySelectorAll('.reveal-up, .reveal-left');

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const el = entry.target;
        const delay = prefersReducedMotion ? 0 : parseInt(el.dataset.delay || '0');
        setTimeout(() => el.classList.add('visible'), delay);
        observer.unobserve(el);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  revealElements.forEach(el => observer.observe(el));

  // ============================================================
  // SMOOTH ACTIVE NAV LINK HIGHLIGHT
  // ============================================================
  const sections = document.querySelectorAll('section[id]');
  const navLinksAll = document.querySelectorAll('.site-nav-link');

  const sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const id = entry.target.getAttribute('id');
        navLinksAll.forEach(link => {
          link.style.color = link.getAttribute('href') === `#${id}`
            ? 'var(--ntl-text)'
            : '';
        });
      }
    });
  }, { threshold: 0.4 });

  sections.forEach(s => sectionObserver.observe(s));

  // ============================================================
  // THE GATE LADDER — the desk's own table, walked through four answers
  //
  // This is NOT an animation of a gate table. It is THE gate table — window.NtlMarketMap,
  // the same module and the same builders the Trading Desk renders — handed a frozen feed
  // through `data` and then a sequence of frozen answers through setGateLadder(). What you
  // see move is the component repainting, exactly as it repaints on the desk when the
  // engine's answer changes. There is no second renderer and no mock-up, so the example on
  // the marketing page cannot come to look different from the product.
  //
  // Why frozen rather than live: the real feed (/MarketStatus/Regimes) and the real
  // evaluation (/MarketStatus/FilterGrid) are both Admin-only, and FilterGrid cannot be
  // opened up — it is the general-purpose engine, and answering an anonymous visitor's
  // arbitrary rules is precisely what MarketStatusController's own comment refuses. So the
  // ANSWER is frozen and says so in the ladder's NAME ("Example — …", the one line that is on
  // screen wherever the table is read), while the renderer, the words and the layout are all
  // the live ones.
  //
  // The four frames are the whole pitch: eleven markets classified, four clear the daily
  // gate, three the hourly, two the 15-minute, one the entry. Nobody is dropped — the real
  // table lists every market that got past gate 1 and reports where it stopped, so the
  // narrowing shows in the ticks, the header chips and the 3/3 row rising to the top. A
  // version that deleted the stragglers would be inventing a behaviour to make a point.
  // ============================================================
  (function () {
    const host = document.getElementById('mkt-list');
    if (!host || !window.NtlMarketMap || !window.NtlGatePanel) return;

    const MM = window.NtlMarketMap;

    // The axis every bitstring below is indexed by. The real feed's own column order.
    const AXIS = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1'];
    const bit = (tf) => AXIS.map((t) => (t === tf ? '1' : '0')).join('');

    // Eleven markets across four asset classes, so the table reads as a watchlist rather than
    // a forex demo. symbol-names.js (loaded on this layout, ahead of market-map.js) turns
    // XAUUSD into "Gold USD" and BTCUSD into "Bitcoin USD", exactly as on the desk; the FX
    // pairs and the indices have no nickname and print as tickers, also as on the desk. The
    // page names no market itself either way.
    const MARKETS = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'EURGBP',
                     'XAUUSD', 'US30', 'NAS100', 'BTCUSD', 'ETHUSD'];

    // The frozen feed. Gate cells never read a market's per-timeframe regime — only the
    // non-ladder grid does — so `timeframes` can be empty and the blob stays honest about
    // carrying no classification data at all.
    const FEED = {
      hasRecentData: true,
      totalPairs: MARKETS.length,
      pairs: MARKETS.map((symbol) => ({ symbol, category: '', timeframes: {} }))
    };

    // ── The ladder, from the panel's OWN starter ────────────────────────────────────────
    // Not written out here. `starters()[0]` is '1D → 1h → 15m example' — a real spec, built
    // by the panel from the live catalogue — and `summarize()` is the same call the desk's
    // metaOf makes to turn a spec into the words a gate heading and a gate cell say. So every
    // phrase on this table is the catalogue's own, and a condition whose wording changes in
    // C# changes here too, without anyone editing the homepage.
    //
    // It is also the SAME ladder the builder further down the page opens on, which is the
    // point: a visitor watches this table resolve and then finds the rules behind it already
    // loaded in the panel.
    //
    // An earlier version hand-wrote the rules and a `phrases` map beside them. The rules were
    // close enough to pass, but the phrases were invented, and the cells ended up printing
    // raw condition KEYS — "Band", "BullTransition", "FvgEntry" — because the short form
    // reads condTags first and nothing had pushed a tag map in (see setConditionTags below).
    const STARTER = (window.NtlGatePanel.starters() || [])[0];
    if (!STARTER || !STARTER.spec || !(STARTER.spec.gates || []).length) return;

    // …plus a FOURTH gate, because every built-in starter is three and a three-gate ladder
    // under-sells a thing whose whole point is that you choose the depth. Built with the
    // panel's own newRule() from the live catalogue rather than hand-written, exactly as the
    // starters themselves are, so this gate's words come from the same place as the other
    // three and cannot be a phrase nobody at NTL would use.
    //
    // A fair value gap on the 5-minute, under the 15m execution gate: it continues the
    // top-down ladder the starter sets up (1d → 1h → 15m) instead of restating one of it.
    //
    // ⚠️ The modifier keys are FvgEntry's OWN — fvgDir and fvgRel, not the `dir` that most
    // conditions take, and fvgDir's values are '0'/'1' rather than 'bull'/'bear'. A key or a
    // value this condition does not recognise is SILENTLY DROPPED and the rule widens: the
    // first version of this gate passed `dir: 'bull'` and read "A fair value gap is present",
    // a side-less rule under three bullish ones, with nothing anywhere to say so. If you edit
    // this, read the condition's own entry in MarketFilterCatalogue.cs first — the phrase the
    // heading prints is the check, and it names every modifier that took.
    const SPEC = {
      mode: 'gates', v: 1, sides: false, bothSides: false,
      gates: STARTER.spec.gates.concat([
        { key: 'g' + (STARTER.spec.gates.length + 1), conn: 'AND', tfs: ['M5'],
          rules: [window.NtlMarketFilter.newRule('FvgEntry', { fvgDir: '0', fvgRel: 'inside' })].filter(Boolean) }
      ])
    };

    const SAID = {};
    (window.NtlGatePanel.summarize(SPEC) || []).forEach((p) => { SAID[p.gate] = p; });

    const GATES = SPEC.gates.map((g, i) => {
      const p = SAID[i + 1] || {};
      return {
        key: g.key || ('g' + (i + 1)),
        tfs: g.tfs || [],
        conn: p.conn || 'and',
        rules: p.rules || [],
        phrases: p.phrases || {}
      };
    });

    // Which conditions each gate is built from — the keys the answer's ruleBits are indexed
    // by. Read off the spec rather than listed again, so a starter that gains a condition
    // gains it in the cells too.
    const CONDS = SPEC.gates.map((g) => {
      const seen = {};
      (g.rules || []).forEach((r) => { if (r && r.cond) seen[r.cond] = 1; });
      return Object.keys(seen);
    });

    // Who reaches each gate. Index 0 is unused so REACH[k] reads as "the markets that cleared
    // gate k" — the frames below are all 1-based, as the product's own gate numbering is.
    //
    // FOUR clear gate 1, not seven (operator, 2026-09-22). The table lists every market that
    // got past gate 1, so this list IS its height — and 4 → 3 → 2 → 1 narrows one market per
    // gate, the plainest version of the pitch. Still three asset classes (FX, gold, an index),
    // so it reads as a watchlist rather than a forex demo.
    //
    // THE LAST GATE GOES TO GBPUSD, NOT EURUSD, so the finish has movement in it. Rows sort by
    // gates matched and ties keep the feed's order (EURUSD above GBPUSD), so GBPUSD taking
    // gate 4 overtakes EURUSD on the last beat — the second row swap of the sequence, after
    // NAS100 passing XAUUSD at gate 2. A EURUSD win happened in the top row and moved nothing.
    const REACH = [[],
      ['EURUSD', 'GBPUSD', 'XAUUSD', 'NAS100'],
      ['EURUSD', 'GBPUSD', 'NAS100'],
      ['EURUSD', 'GBPUSD'],
      ['GBPUSD']
    ];

    // One frame = one answer. Built rather than written out: three lists and the rest is
    // arithmetic, so the header chips and the cells can never disagree with each other the way
    // two hand-kept copies of the same numbers would.
    function frame(n) {
      const reach = REACH;
      const stage = {}, cells = {}, rules = {};
      GATES.forEach((g) => { cells[g.key] = {}; rules[g.key] = {}; });

      MARKETS.forEach((sym) => {
        let got = 0;
        for (let k = 1; k <= n; k++) if (reach[k].indexOf(sym) >= 0) got = k;
        stage[sym] = got;
      });

      for (let k = 1; k <= n; k++) {
        const key = GATES[k - 1].key, tf = GATES[k - 1].tfs[0];
        reach[k].forEach((sym) => {
          cells[key][sym] = bit(tf);
          CONDS[k - 1].forEach((cond) => { (rules[key][cond] = rules[key][cond] || {})[sym] = bit(tf); });
        });
      }

      return {
        id: 1,
        stage: stage,
        gateCells: cells,
        ruleBits: rules,
        gateCounts: GATES.reduce((out, g, i) => {
          out[g.key] = n >= i + 1 ? reach[i + 1].length : 0;
          return out;
        }, {}),
        // What the next gate still wants, for the markets standing at it. The real answer
        // carries this and the cells read it for their "what this gate asks for" line.
        blocking: {},
        builtLean: 'bull'
      };
    }

    // Three frames, starting at ONE. There was a frame 0 — no ladder, an empty table — and it
    // opened the section on a blank list with nothing ticked, which is the least interesting
    // moment the product has and the one a visitor is most likely to catch. The first thing on
    // screen is now the daily gate already answered: four markets in, one column green.
    //
    // It also holds the row count still. Every gate after the first narrows an existing list
    // rather than adding to it (G3 ⊂ G2 ⊂ G1), so from here the table never changes height and
    // the page underneath never shifts.
    const FIRST = 1, LAST = GATES.length;
    const FRAMES = {};
    for (let n = FIRST; n <= LAST; n++) FRAMES[n] = frame(n);

    // WORDS is NtlGatePanel's own table — the same one the desk passes — so "2 of 3 gates",
    // "Gates matched" and "Progress" are the product's wording and not a marketing paraphrase
    // of it. The compliance review that set those words (see
    // GatedAlertWording.cs) applies here by construction rather than by anyone remembering.
    //
    // ONE ENTRY IS BLANKED: sortedBy, the label in the corner of the name bar that names the
    // order a gate table is already in ("Gates matched"). On the desk it answers a question a
    // member with several saved ladders has; there is one table here and it is ordered the
    // only way it could be, so the label was a caption on an answer to nothing (operator
    // ruling, 2026-09-21). Blanked rather than hidden in CSS: gateHeadHtml writes the corner
    // only when the word exists, so nothing is left in the DOM or the accessibility tree.
    //
    // A COPY, not an edit. window.NtlGatePanel.WORDS is the module's own object and the desk
    // reads the same one — assigning into it from the homepage would blank the desk's label
    // for anyone who loaded both.
    const WORDS = Object.assign({}, window.NtlGatePanel.WORDS, { sortedBy: '' });

    // THE LADDER'S NAME, which is the table's top tier and the first line of the example
    // Telegram notifications. Deliberately generic since 2026-09-25 (operator): "Your multi
    // timeframe gate system" says what the table IS — a ladder of the visitor's own, gated
    // across timeframes — rather than naming one strategy. It was "Daily pullback, 5m FVG
    // entry", and before that "Daily bias, 15m entry", which fell out of step with the gates
    // when the 5m FVG gate was appended; a generic name cannot.
    //
    // ⚠️ IT IS ALSO THE ONLY THING ON THIS CARD THAT CAN SAY THE ANSWERS ARE MADE UP. The tag
    // that used to sit above the table, and the "not a trade recommendation or trade signal"
    // line that sat under it, have both gone. What is on screen is real market names with
    // ticks against them and Telegram alerts coming off it, and a visitor cannot tell by
    // looking that the ANSWER is frozen — the renderer, the words and the layout are all the
    // live ones. Anyone renaming this should decide, deliberately, where that gets said.
    // The table's title is the route through the gates, in the builder's header style above it
    // (operator, 2026-10-18: home.css labels it "Gate system" and tags it "Example"); the
    // Telegram messages keep the system's name, as the bot sends it.
    const SYSTEM_NAME = 'Your multi timeframe gate system';
    const META = { timeframes: AXIS, gates: GATES, words: WORDS, name: '1D \u2192 1h \u2192 15m \u2192 5m', id: 1 };

    // ── THE TELEGRAM ALERTS ─────────────────────────────────────────────────────────
    // The offer panel says a ticked gate can message you ("as each gate opens"). This shows
    // that instead: as each deep gate fills, a Telegram notification slides into a stack at
    // the bottom-right of the window, sits for a few seconds and goes, the way a real one does.
    //
    // ONE TOAST PER GATE, naming every market that cleared it — the way the bot sends it:
    // TrendAlertService.BuildGatedAlertMessage puts every market that reached the rung on the
    // same evaluation into ONE message, one per line, alphabetical. So gate 3 lands one
    // notification listing two markets, and gate 4 a second one under it.
    //
    // WHICH GATES: the deep two, by operator ruling (2026-09-21). It is also the honest
    // demonstration — gate 1 is most of a watchlist, and an alert on it is the notification
    // anybody would turn off within a day, so a page that showed one would be advertising the
    // wrong use of the feature.
    //
    // OUTSIDE THE TABLE. Every frame is host.innerHTML = …, so a node inside it cannot
    // survive one, let alone transition across it. The stack is a sibling and nothing rewrites
    // it but this.
    //
    // The words are the PRODUCT'S: the real message opens with the traffic light and the
    // ladder's name, then GatedAlertWording.Headline (WORDS.headline here), then the market,
    // and a notification preview shows exactly that with the newlines folded. The page
    // invents no alert text.
    const ALERT_GATES = [3, 4];
    // The pace (operator, 2026-09-25: the first cut read as frantic). Long enough per gate to
    // see which rows it kept and read a notification, and a long hold on the finished table
    // so the replay is a pause, not a flicker. Mutable only so reel mode can speed it up.
    const pace = {
      step: 3200,    // between one gate filling and the next
      hold: 10000,   // the finished table, before the loop starts again
      toast: 6500    // how long one notification stays up before it retires itself
    };
    const TOAST_OUT_MS = 340;  // matches the leaving transition in home.css
    const TOAST_MAX = 3;       // the stack's ceiling; the sequence never needs a fourth

    const tgEl = document.getElementById('gates-tg');
    const tgTpl = document.getElementById('gates-tg-tpl');
    // Every pending add and every pending retire, so a reset can cancel the lot. Without this
    // a toast scheduled just before the loop restarts lands on the next pass.
    let toastTimers = [];

    function toastAfter(ms, fn) { toastTimers.push(window.setTimeout(fn, ms)); }

    function dropToast(el) {
      if (!el || !el.parentNode) return;
      el.classList.remove('is-in');
      el.classList.add('is-out');
      // Left in the DOM for the length of its own fade, then taken out so the stack closes up.
      toastAfter(TOAST_OUT_MS, function () { if (el.parentNode) el.parentNode.removeChild(el); });
    }

    function pushToast(title, text, markets) {
      if (!tgEl || !tgTpl) return;
      const el = tgTpl.content.firstElementChild.cloneNode(true);
      const t1 = el.querySelector('.tgn-title');
      const t2 = el.querySelector('.tgn-text');
      const t3 = el.querySelector('.tgn-markets');
      if (t1) t1.textContent = title;
      if (t2) t2.textContent = text;
      if (t3) t3.textContent = markets || '';
      tgEl.appendChild(el);

      // The oldest goes when the stack is full, rather than the card growing a fourth line of
      // notifications it never has room for.
      // Two on a phone, where the stack spans the screen and three would cover half of it.
      const max = window.matchMedia && window.matchMedia('(max-width: 575.98px)').matches ? 2 : TOAST_MAX;
      while (tgEl.querySelectorAll('.gates-tg-toast:not(.is-out)').length > max)
        dropToast(tgEl.querySelector('.gates-tg-toast:not(.is-out)'));

      // Born at opacity 0 (home.css); .is-in on the NEXT frame is what gives the transition a
      // from-state to travel from. Set in the same frame, the browser coalesces both into no
      // change at all and the toast simply appears — the same trick the row FLIP below uses.
      if (noMotion()) { el.classList.add('is-in'); return; }
      requestAnimationFrame(function () { requestAnimationFrame(function () { el.classList.add('is-in'); }); });
      toastAfter(pace.toast, function () { dropToast(el); });
    }

    function clearToasts() {
      toastTimers.forEach(window.clearTimeout);
      toastTimers = [];
      if (tgEl) tgEl.textContent = '';
    }

    // Reel mode (the bottom of this file) sends ONE alert, in plainer words, when the last gate
    // fills — a video has a second to be read, not the bot's full message twice.
    let reelAlerts = false;

    function showAlerts(n) {
      if (!tgEl) return;
      if (reelAlerts) {
        const won = (REACH[LAST] || []).slice().sort();
        // One gate-step after the last gate, as if it were the next beat of the ladder. Through
        // toastAfter, so a reset in the meantime cancels it.
        if (n === LAST && won.length) toastAfter(pace.step, function () {
          pushToast(won.join(' · ') + ' matched all ' + LAST + ' gates', '', '');
        });
        return;
      }
      if (ALERT_GATES.indexOf(n) < 0) return;
      // REACH[n] is exactly who cleared gate n on this frame — the same list the ticks and the
      // header chips are built from, so a toast can never name a market the table did not
      // just credit.
      const said = typeof WORDS.headline === 'function' ? WORDS.headline(n, LAST) : 'Your rule matched: gate ' + n + ' of ' + LAST;
      // The message's first line: the traffic light, then the ladder's name.
      const title = '\u{1F6A6} ' + SYSTEM_NAME;
      // Sorted as the server sorts them, and on a line of their own under the headline, as in
      // the message itself.
      const markets = (REACH[n] || []).slice().sort();
      if (!markets.length) return;
      pushToast(title, said, markets.join(' · '));
    }

    // ── What actually animates ───────────────────────────────────────────────────────
    // Every render is `host.innerHTML = …`, so no CSS transition survives one: the cell you
    // wanted to fade in is a brand-new node that starts at its finished style. Two mechanics
    // work around that, and between them they are the whole effect.
    //
    // 1. TICKS. A keyframe on .is-gate-met would replay for every matched cell on every
    //    frame. So we diff: onRender adds .is-justmet only to cells that were not matched in
    //    the previous frame, and home.css hangs a one-shot animation off that.
    // 2. ROWS. FLIP around the repaint — measure where each row was, let it repaint, measure
    //    where it landed, put it back with a transform and release it on the next frame.
    //    market-map.js's own glide() does the same thing for the band handle.
    //
    // DECLARED BEFORE THE MOUNT, and that is not tidiness. A frozen feed renders INSIDE
    // mount() rather than in a fetch callback, so onRender fires while the lines below the
    // mount call are still in their temporal dead zone. Written the other way round this
    // threw on the first paint and the whole section stayed on its empty frame.
    let prevMet = {};

    // THE FIRST PAINT LANDS FINISHED. Every cell of the opening frame is new by definition —
    // there is no previous frame to diff against — so the table used to arrive with every
    // row's ticks dropping in at once, which is the one moment in the sequence that says
    // nothing: it is the table BEING DRAWN, not a gate being answered. The diff is the whole
    // point of the effect (a cell animates because its gate just matched), and on frame one
    // that claim is not true of anything.
    //
    // So the opening render only records what is matched; from the second render on, a cell
    // that was not matched last time animates, as before. The loop's reset clears prevMet and
    // this stays false, so the replay still lights gate 1 — there, the ticks really are
    // arriving.
    let firstPaint = true;

    // A small, stable string hash (FNV-1a) — for the tick order below, not for anything secret.
    function hash(str) {
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
      return h >>> 0;
    }

    // ── Reel mode's gate headings ────────────────────────────────────────────────────
    // On a phone the desk drops the heading tier, so nothing says what a tick is FOR — and the
    // reel is shown at phone width. This puts one row back, under the table's name: each gate's
    // timeframe over the conditions that make it that gate ("1d / Trend", "1h / ChoCh",
    // "5m / FVG"), in the catalogue's own short tags. One label per COLUMN, not per tick —
    // every tick in a column means the same thing.
    //
    // A condition that recurs in several gates (Prem/Disc, here) is left off: it is context,
    // not what tells one gate from the next, and a narrow column has no room for it.
    //
    // Re-inserted after every render, because each render rewrites the table's innerHTML.
    let gateHeads = false;
    let gateHeadHtml = null;

    // What each gate asks for, as { tf, words } — shared by reel mode's row and the page's own
    // column labels (labelGateHeads, below), so the two can never describe a gate differently.
    let gateLabelList = null;
    function gateLabels() {
      if (gateLabelList) return gateLabelList;
      let tags = {};
      try { const MF = window.NtlMarketFilter; if (MF && MF.conditionTags) tags = MF.conditionTags() || {}; } catch (e) { /* bare keys */ }
      const uses = {};
      CONDS.forEach((cs) => cs.forEach((c) => { uses[c] = (uses[c] || 0) + 1; }));
      gateLabelList = GATES.map((g, i) => {
        const own = CONDS[i].filter((c) => uses[c] === 1);
        return {
          tf: (g.tfs || []).map((t) => MM.tfLabel(t)).join(', '),
          words: (own.length ? own : CONDS[i]).map((c) => tags[c] || c).join(' · '),
          // The same words one per element, for the page's column labels: on a phone they stack
          // and the separator goes, rather than wrapping a lone '·' onto a line of its own.
          list: (own.length ? own : CONDS[i]).map((c) => tags[c] || c)
        };
      });
      return gateLabelList;
    }
    const escHtml = (t) => String(t).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

    function gateHeadRow() {
      if (gateHeadHtml != null) return gateHeadHtml;
      gateHeadHtml = '<div class="reel-gheads" aria-hidden="true">' + gateLabels().map((l) =>
        '<span class="reel-ghead"><span class="reel-ghead-tf">' + escHtml(l.tf) + '</span>' +
          '<span class="reel-ghead-what">' + escHtml(l.words) + '</span></span>'
      ).join('') + '</div>';
      return gateHeadHtml;
    }

    // ── The page's gate labels (2026-10-14) ──────────────────────────────────────────
    // The hero's column headings said only "Gate 1 … Gate 4", so a first-time visitor saw
    // ticks without knowing what they were ticks FOR, and the one place that said was a small
    // caption under the table. Each heading now carries its gate's timeframe and conditions
    // under its number — the same words reel mode's row prints. Inside the existing heading
    // cells rather than as a row of their own, so each label sits over its own column whatever
    // the table's widths. Re-applied after every render (each one rewrites the table).
    function labelGateHeads() {
      const labels = gateLabels();
      host.querySelectorAll('.mkt-gth[data-gate]').forEach((th) => {
        if (th.querySelector('.hero-gth-what')) return;
        const l = labels[Number(th.getAttribute('data-gate')) - 1];
        if (!l) return;
        th.insertAdjacentHTML('beforeend',
          '<span class="hero-gth-what"><span class="hero-gth-tf">' + escHtml(l.tf) + '</span> ' +
            l.list.map((w) => '<span class="hero-gth-w">' + escHtml(w) + '</span>').join('<span class="hero-gth-sep"> · </span>') + '</span>');
      });
    }

    function addGateHeads() {
      const table = host.querySelector('.mkt-gtable');
      if (!table || table.querySelector('.reel-gheads')) return;
      const name = table.querySelector('.mkt-gheadblock');
      if (name) name.insertAdjacentHTML('afterend', gateHeadRow());
      else table.insertAdjacentHTML('afterbegin', gateHeadRow());
    }

    function markNewCells() {
      if (gateHeads) addGateHeads();
      labelGateHeads();
      const cells = host.querySelectorAll('.mkt-gcell.is-gate-met');
      // ⚠️ MOUNT PAINTS ONCE BEFORE THE FIRST FRAME. A frozen feed renders inside mount(),
      // with a feed but no ladder, so onRender fires on a table that has no gate cells at all.
      // Spending the first-paint exemption there left the real opening frame to animate every
      // one of its ticks — which is the bug this whole flag exists to prevent.
      if (firstPaint && !cells.length) return;
      const nextMet = {};
      const fresh = [];
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i];
        const k = c.getAttribute('data-sym') + '|' + c.getAttribute('data-gate');
        nextMet[k] = 1;
        if (!firstPaint && !prevMet[k]) { c.classList.add('is-justmet'); fresh.push({ c: c, h: hash(k) }); }
      }
      // NO TWO MARKETS OPEN A GATE AT THE SAME INSTANT — real ones don't. Each new tick gets its
      // own delay (home.css reads --gate-d): an order that is not simply top-to-bottom, plus
      // jitter so the gaps are uneven. Hashed from market + gate rather than random, so every
      // pass (and every take of a Reel) plays identically.
      //
      // The gap SCALES WITH THE PACE (operator, 2026-09-29): a fixed 100ms read as a clear
      // stagger in the Reel (a gate every ~500ms) but as all-at-once on the page (a gate every
      // 3.2s). ~9% of a step keeps the Reel's rhythm everywhere — ~290ms apart here, a little
      // slower, the same look — and never under the Reel's own 100ms.
      const gap = Math.max(100, Math.round(pace.step * 0.09));
      const jitter = Math.round(gap * 0.3);
      fresh.sort(function (a, b) { return a.h - b.h; });
      fresh.forEach(function (f, i) { f.c.style.setProperty('--gate-d', (i * gap + f.h % (jitter + 1)) + 'ms'); });
      prevMet = nextMet;
      firstPaint = false;
    }

    // charts:false   — the cells open nothing here, and say so by not being pressable.
    // gateMinimal    — the hero's reading (operator, 2026-09-24): each heading is "Gate N · tf"
    //                  over the gate's short tags, each cell is its marker alone (a tick, a
    //                  ring at the gate a market stands at, a dot beyond), and the words a
    //                  cell used to print ride in a tip the map draws itself. It
    //                  implies gateStatic, so the fold bar and headings are not controls and
    //                  this page writes none of the desk's remembered preferences. (The phone
    //                  still loses the heading tier at 900px and under, and reads the ticks
    //                  alone.)
    // data           — frozen, so there is no fetch and the first paint is the table.
    // reshuffle:false — the map's own filter-change animation stays off: the frames here are
    //                  animated by flip() and .is-justmet below, and the map's version also ran
    //                  on the opening setGateLadder, over the feed's grid layout — so the hero
    //                  faded in an 11-row grid and faded most of it out again (2026-10-12).
    const map = MM.mount({
      container: host,
      mode: 'compact',
      pollMs: 0,
      charts: false,
      gateMinimal: true,
      reshuffle: false,
      data: FEED,
      onRender: markNewCells
    });

    // ── The words in the cells ───────────────────────────────────────────────────────
    // The map keeps NO reader of the catalogue: both of these are pushed in, exactly as the
    // desk pushes them, so there is one parser of the vocabulary and the cells cannot come to
    // call a condition something the panel doesn't.
    //
    //   tags   the SHORT word a cell wears — "FvgEntry" → "FVG". Straight from the
    //          catalogue's own Tag field, which is the one place FilterCondition.Tag reaches
    //          the browser.
    //   labels the full name, for the tooltip and for the "what this gate still wants" line
    //          on a gate a market has not reached.
    //
    // Without them the short form falls all the way through to the raw condition KEY, and the
    // table advertises the product in identifiers: "Band", "BullTransition", "TransBull".
    // Guarded the same way the desk guards it — untagged cells are a cosmetic loss, and not
    // worth taking the rest of the section down for.
    try {
      const MF = window.NtlMarketFilter;
      if (MF && MF.conditionTags) map.setConditionTags(MF.conditionTags());
      if (MF && MF.grouped) {
        const labels = {};
        MF.grouped().forEach((grp) => {
          (grp.items || []).forEach((c) => { labels[MM.field(c, 'Key')] = MM.field(c, 'Label'); });
        });
        map.setConditionLabels(labels);
      }
    } catch (e) { /* cells simply go untagged */ }

    function rowRects() {
      const out = {};
      const rows = host.querySelectorAll('.mkt-mrow');
      for (let i = 0; i < rows.length; i++) out[rows[i].getAttribute('data-sym')] = rows[i].getBoundingClientRect().top;
      return out;
    }

    // The FLIP half. `was` is measured before the repaint; anything that existed then and has
    // moved is put back where it was and released, so it travels instead of jumping. A row
    // that is new to the table has no `was` and simply appears — its cells' own entrance
    // animation covers it.
    function flip(was) {
      if (noMotion()) return;
      const rows = host.querySelectorAll('.mkt-mrow');
      for (let i = 0; i < rows.length; i++) {
        const el = rows[i], sym = el.getAttribute('data-sym');
        const from = was[sym];
        if (from == null) continue;
        const dy = from - el.getBoundingClientRect().top;
        if (!dy) continue;
        el.style.transition = 'none';
        el.style.transform = 'translateY(' + dy + 'px)';
      }
      // Two frames, not one: the first commits the inverted position, the second releases it.
      // Released in the same frame it was set, the browser coalesces both into no change at
      // all and nothing moves.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          for (let i = 0; i < rows.length; i++) { rows[i].style.transition = ''; rows[i].style.transform = ''; }
        });
      });
    }

    function noMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    // Which frame is on screen. The loop reads it so it never repaints a frame that is already
    // there — see run(), where that repaint was visible as a slide.
    let shown = 0;

    function show(n) {
      const was = rowRects();
      map.setGateLadder(FRAMES[n], META);
      shown = n;
      showAlerts(n);
      flip(was);
    }

    // ── The loop ─────────────────────────────────────────────────────────────────────
    // Starts when the table is on screen, walks the frames, holds the finished state, then
    // resets and goes again. Under reduced motion it renders the answer once and stops: the
    // point of the section is the shape of the result, and that is legible standing still.
    let timer = 0;
    let running = false;

    // Scrolled away, or the tab hidden: the table stops and the stack goes with it. A toast
    // left hanging would still be there on the way back, minutes later, claiming a market
    // reached a gate just now.
    function stop() { window.clearTimeout(timer); timer = 0; running = false; clearToasts(); }

    function run(n) {
      // ⚠️ NOT AN UNCONDITIONAL REPAINT. The opening frame is painted the moment this file
      // runs (see show(FIRST) below), and the observer then starts the loop AT that same
      // frame — so the first thing the loop used to do was re-render a table that was already
      // on screen, identically.
      //
      // That repaint was not free. Every render is an innerHTML rewrite, so the FLIP measures
      // the old rows, throws them away, measures the new ones and animates the difference —
      // and between those two measurements the page is still settling (a webfont swapping, an
      // image above the fold arriving), so the difference is rarely zero. Every row then slid
      // into place for no reason anybody could see, on load, every time.
      //
      // A frame that is already shown is simply left alone, and the loop waits for the next.
      if (n !== shown) show(n);
      if (n < LAST) { timer = window.setTimeout(function () { run(n + 1); }, pace.step); return; }
      // Held long enough to read the finished table before it resets — the reset is the one
      // moment that could read as a glitch rather than as a replay. Clearing prevMet is what
      // lets the first gate's ticks land again rather than the replay starting half-done.
      timer = window.setTimeout(function () { prevMet = {}; clearToasts(); run(FIRST); }, pace.hold);
    }

    if (noMotion()) {
      show(LAST);
      return;
    }

    // THE OPENING FRAME IS PAINTED NOW, not when the observer fires. Until it does, the map
    // has a feed but no ladder, and a feed with no ladder renders its OTHER layout: eleven
    // markets by seven timeframes, every cell an em-dash, because this frozen blob carries no
    // per-timeframe regimes. A visitor who met the section before the observer caught up —
    // or on any browser where it never did — saw a grid of dashes where the gate table should
    // be, with nothing to say it was still coming.
    //
    // The observer's job is the ANIMATION, and only that. The table is correct from the first
    // paint whether it ever fires or not.
    show(FIRST);

    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && !running) { running = true; run(FIRST); }
        else if (!e.isIntersecting && running) { stop(); }
      });
    }, { threshold: 0.25 });

    io.observe(host);

    // Nothing to repaint while the tab is hidden, and a timer that kept firing would come
    // back mid-sequence.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
    });

    // Reel mode: back to gate 1 NOW (the table is hidden while the headline comes in), then
    // after `delay` ms replay from an empty first gate, so gate 1's ticks land on camera, one
    // gate every `step` ms. Returns the ms until the alert lands.
    reelHooks.ladder = function (step, delay) {
      pace.step = step;
      // Four steps, and never below 3.5s: the hold is where the alert and the actions are read.
      pace.hold = Math.max(3500, step * 4);
      // The one alert stays up through the hold; the loop's reset clears it.
      pace.toast = pace.hold;
      reelAlerts = true;
      gateHeads = true;
      stop();
      running = true;
      show(FIRST);
      timer = window.setTimeout(function () {
        prevMet = {};
        firstPaint = false;
        show(FIRST);
        if (FIRST < LAST) timer = window.setTimeout(function () { run(FIRST + 1); }, pace.step);
      }, delay);
      // Until the alert: one step per gate after the first, and one more for the alert.
      return delay + (LAST - FIRST + 1) * pace.step;
    };
  })();

  // ============================================================
  // "WHAT YOU CAN BUILD GATES FROM" — collapsible groups, PHONES ONLY (2026-10-14)
  //
  // On a phone the six groups stack into one column of 27 names, more than a screen between the
  // builder and the button. There, each group's title becomes a toggle and its list starts shut.
  // On anything wider nothing changes: the lists are always open and the titles are plain text.
  //
  // The names stay in the server-rendered HTML either way (search engines read them; the builder
  // above is drawn by JS and they read nothing there), and without this script every list is
  // simply open. Crossing the breakpoint re-syncs, so rotating a tablet does the right thing.
  // ============================================================
  (function () {
    const list = document.querySelector('.gates-groups-list');
    if (!list || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 575.98px)');
    const groups = Array.prototype.slice.call(list.querySelectorAll('.gates-group'));

    function setOpen(g, open) {
      g.classList.toggle('is-open', open);
      if (mq.matches) g.querySelector('dt').setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    groups.forEach(function (g, i) {
      const dt = g.querySelector('dt'), dd = g.querySelector('dd');
      if (!dt || !dd) return;
      dd.id = dd.id || 'gates-group-' + (i + 1);
      dt.addEventListener('click', function () {
        if (mq.matches) setOpen(g, !g.classList.contains('is-open'));
      });
      dt.addEventListener('keydown', function (e) {
        if (!mq.matches || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        setOpen(g, !g.classList.contains('is-open'));
      });
    });

    function sync() {
      list.classList.toggle('is-collapsible', mq.matches);
      groups.forEach(function (g) {
        const dt = g.querySelector('dt'), dd = g.querySelector('dd');
        if (!dt || !dd) return;
        if (mq.matches) {
          dt.setAttribute('role', 'button');
          dt.setAttribute('tabindex', '0');
          dt.setAttribute('aria-controls', dd.id);
          dt.setAttribute('aria-expanded', g.classList.contains('is-open') ? 'true' : 'false');
        } else {
          ['role', 'tabindex', 'aria-controls', 'aria-expanded'].forEach(function (a) { dt.removeAttribute(a); });
        }
      });
    }

    if (mq.addEventListener) mq.addEventListener('change', sync); else mq.addListener(sync);
    sync();
  })();

  // ============================================================
  // "HOW NTL FITS INTO YOUR DAY", STEP 1 — a corner of the Market Map
  //
  // Drawn by NtlMarketMap.cellHtml, the desk's own cell, so the colours and the words are the
  // map's and cannot drift from it. The READINGS are made up, like the hero table's answers,
  // and the step says so by being an illustration of a routine rather than a feed: there is
  // no live data on this page (the real feed is Admin-only — see the ladder's note above).
  //
  // Every label is one MapRegime can produce (SmcLabels.cs), paired with its own RegimeClass,
  // because a cell's tint comes from the class and its words from the label. Four timeframes,
  // not seven: the card is a third of the page wide.
  //
  // ONE MARKET THROUGH ALL THREE STEPS (2026-10-14). EURUSD is the market the section follows:
  // its row here is the one left lit (the others take the map's own dimmed state), the ladder
  // in step 2 is EURUSD's, and the alert in step 3 names it. Its readings are chosen to agree
  // with that ladder — 1D Bullish Structure (gate 1, trend is bullish), 1h Bullish ChoCh
  // Mitigation (gate 2, a bullish change of character has happened), 15m Bullish Retracement
  // (gate 3 not yet: price is pulling back but is not in the gap). Change one, check the other
  // two (Index.cshtml, #how).
  // ============================================================
  (function () {
    const host = document.getElementById('how-map');
    const MM = window.NtlMarketMap;
    if (!MM || !MM.cellHtml) return;

    const TFS = ['M15', 'H1', 'H4', 'D1'];
    const R = {
      bull:   { RegimeClass: 'bullish', Label: 'Bullish Structure' },
      bear:   { RegimeClass: 'bearish', Label: 'Bearish Structure' },
      bullRt: { RegimeClass: 'transitioning-bullish', Label: 'Bullish Retracement' },
      bullMt: { RegimeClass: 'transitioning-bullish', Label: 'Bullish ChoCh Mitigation' },
      bearRt: { RegimeClass: 'transitioning-bearish', Label: 'Bearish Retracement' },
      bearIm: { RegimeClass: 'bearish', Label: 'Bearish Impulse' }
    };
    const FOCUS = 'EURUSD';
    const ROWS = [
      ['EURUSD', [R.bullRt, R.bullMt, R.bull, R.bull]],
      ['XAUUSD', [R.bear, R.bearRt, R.bull, R.bull]],
      ['NAS100', [R.bearIm, R.bear, R.bearRt, R.bull]]
    ];
    const name = (s) => (window.NtlSymbolNames ? window.NtlSymbolNames.nameOf(s) : s);

    // One renderer for both maps. `focus` names the market left lit (the others take the map's
    // dimmed state); null lights every row.
    const draw = (el, rows, focus) => {
      el.innerHTML =
        '<div class="how-map-row how-map-head"><span></span>' +
          TFS.map((tf) => '<span class="how-map-tf">' + MM.esc(MM.tfLabel(tf)) + '</span>').join('') +
        '</div>' +
        rows.map(([sym, cells]) => {
          const lit = !focus || sym === focus;
          return '<div class="how-map-row' + (focus && lit ? ' is-focus' : '') + '"><span class="how-map-sym">' + MM.esc(name(sym)) + '</span>' +
            cells.map((c, i) => MM.cellHtml(c, sym, TFS[i], { inert: true, dimmed: !lit })).join('') +
          '</div>';
        }).join('');
    };
    if (host) draw(host, ROWS, FOCUS);
  })();

  // ============================================================
  // THE HERO'S MARKET MAP (operator, 2026-10-19: "pull the market map and place it" in the hero)
  //
  // Not a drawing: the desk's own module, mounted on the desk's own table shell (_MarketMap in
  // Index.cshtml), exactly as the gate table further down mounts it — mode 'compact' (inert
  // cells, no flags), no charts, no poll, and a FROZEN feed in `data` because the real
  // /MarketStatus/Regimes is Admin-only. So the seven timeframes, the header, the cell colours
  // and the words are the desk's, and cannot drift from it.
  //
  // NO TRADES: setPositions is never called, so no row wears the desk's open-position badge,
  // spine or float-to-top. No flags (compact mode never renders the button). No prices.
  //
  // The READINGS are made up, and the window's caption says so. Every label is one MapRegime
  // can produce (SmcLabels.cs), paired with its own RegimeClass. EURUSD, Gold USD and NAS100
  // agree with the step-1 map below on the four timeframes it shows (15m, 1h, 4h, 1D) — change
  // one, check the other (and the EURUSD ladder in step 2).
  //
  // `sort: 'server'` keeps this list's order.
  //
  // THE ENTRANCE IS THE DESK'S (operator, 2026-10-19): `entrance: true` plays NtlMarketMap's own
  // refresh wave (trading-desk.css, .is-entering) on the first paint. The mount waits until the
  // window is part-way through its own arrival (home.css, .hero-desk), so the wave is seen rather
  // than spent behind a fade; the skeleton rows _MarketMap ships hold the shape until then.
  // Reduced motion mounts at once and the CSS plays nothing.
  // ============================================================
  (function () {
    const host = document.getElementById('hero-map');
    const MM = window.NtlMarketMap;
    if (!host || !MM || !MM.mount) return;

    const AXIS = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'];
    const C = {
      bB: ['bullish', 'Bullish Bias'],                         sB: ['bearish', 'Bearish Bias'],
      bS: ['bullish', 'Bullish Structure'],                    sS: ['bearish', 'Bearish Structure'],
      bI: ['bullish', 'Bullish Impulse'],                      sI: ['bearish', 'Bearish Impulse'],
      bR: ['transitioning-bullish', 'Bullish Retracement'],    sR: ['transitioning-bearish', 'Bearish Retracement'],
      bM: ['transitioning-bullish', 'Bullish ChoCh Mitigation'], sM: ['transitioning-bearish', 'Bearish ChoCh Mitigation'],
      bC: ['transitioning-bullish', 'Bullish Change of Character'], sC: ['transitioning-bearish', 'Bearish Change of Character']
    };
    //            1m  5m  15m 30m 1h  4h  1D  1W
    const ROWS = [
      ['EURUSD', 'bB bI bR bS bM bS bS bS'],
      ['GBPUSD', 'sR bB bS bS bR bS bS bB'],
      ['USDJPY', 'bI sC sR sS sS sS sB bS'],
      ['XAUUSD', 'bS bB sS sB sR bS bS bS'],
      ['NAS100', 'sI sB sI sS sS sR bS bS'],
      ['US30',   'bB bS bS bI bS bM bS bS'],
      ['BTCUSD', 'sM sB bC bR bS bB sB bB'],
      ['AUDUSD', 'bB bB bS bI bI sB sB sR']
    ];
    const FEED = {
      hasRecentData: true,
      totalPairs: ROWS.length,
      pairs: ROWS.map(([symbol, codes]) => {
        const timeframes = {};
        codes.split(' ').forEach((k, i) => { timeframes[AXIS[i]] = { RegimeClass: C[k][0], Label: C[k][1] }; });
        return { symbol, category: '', timeframes };
      })
    };

    const mount = () => MM.mount({
      container: host,
      mode: 'compact',
      pollMs: 0,
      charts: false,
      reshuffle: false,
      sort: 'server',
      entrance: true,
      data: FEED
    });
    if (prefersReducedMotion) mount(); else window.setTimeout(mount, 650);
  })();

  // ============================================================
  // ARRIVAL MOTION — the page's "calm" entrances (operator, 2026-10-15)
  //
  // Picked from four candidates in a preview (an Apple-style pinned story, wipes, parallax and
  // this): soft fades and small rises, each part playing ONCE as it arrives, nothing tied to
  // the scroll and nothing that holds the page still. The section reveals (.reveal-up) are
  // softened in home.css; this adds the parts that need a script:
  //   · the three steps of #how play in turn — the map fills in column by column, the gates
  //     write and tick one at a time, then the phone comes up and the alert drops onto it;
  //   · the builder and the conditions list fade in when they are reached (home.css,
  //     .is-arrived, held back only under html.home-motion so the page never waits on JS).
  // Reduce-motion visitors get the page as it is, fully drawn, with no hidden starting state.
  // ============================================================
  (function () {
    if (prefersReducedMotion || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('home-motion');

    const clamp = (v) => Math.max(0, Math.min(1, v));
    const seg = (t, a, b) => clamp((t - a) / (b - a));
    const out = (t) => 1 - Math.pow(1 - t, 3);
    // A small overshoot for things that "land" (the map's cells, the alert).
    const back = (t) => 1 + 2.4 * Math.pow(t - 1, 3) + 1.4 * Math.pow(t - 1, 2);
    const tween = (dur, f) => {
      const t0 = performance.now();
      (function frame(now) { const t = clamp((now - t0) / dur); f(t); if (t < 1) requestAnimationFrame(frame); })(t0);
    };
    const onArrive = (el, f, threshold) => {
      if (!el) return;
      const io = new IntersectionObserver((es) => {
        es.forEach((e) => { if (e.isIntersecting) { io.disconnect(); f(); } });
      }, { threshold: threshold || 0.25 });
      io.observe(el);
    };

    // ── The three steps ───────────────────────────────────────────────────────────────
    const how = document.getElementById('how');
    const steps = how ? Array.prototype.slice.call(how.querySelectorAll('.how-step')) : [];
    if (steps.length === 3) {
      // The steps run their own entrance, so the generic one comes off (it would fade them a
      // second time, a beat behind).
      steps.forEach((s) => { s.classList.remove('reveal-up'); s.classList.add('visible'); });
      // Ticks and rings draw themselves: a path one unit long, dashed one unit, offset 1 → 0.
      how.querySelectorAll('.how-ladder .how-lmark svg > *').forEach((p) => {
        p.setAttribute('pathLength', '1');
        p.style.strokeDasharray = '1';
        p.style.strokeDashoffset = '1';
      });

      // Each step as a function of its own time t, 0 → 1.
      const play = [
        function (t) { // 1. the map fills in, column by column
          how.querySelectorAll('.how-map-row').forEach((row, r) => {
            if (row.classList.contains('how-map-head')) { row.style.opacity = out(seg(t, 0, 0.2)); return; }
            const rr = r - 1;
            const sym = row.querySelector('.how-map-sym');
            if (sym) {
              const s = out(seg(t, 0.05 + rr * 0.07, 0.3 + rr * 0.07));
              sym.style.opacity = s;
              sym.style.transform = 'translateX(' + (-8 * (1 - s)) + 'px)';
            }
            row.querySelectorAll('.mkt-tf-box').forEach((c, ci) => {
              const a = 0.12 + ci * 0.16 + rr * 0.07;
              const s = seg(t, a, a + 0.3);
              c.style.opacity = s;
              c.style.transform = 'scale(' + (0.82 + 0.18 * back(s)) + ')';
            });
          });
        },
        function (t) { // 2. the gates are written and ticked, one at a time
          const sym = how.querySelector('.how-ladder-sym');
          if (sym) sym.style.opacity = out(seg(t, 0, 0.15));
          how.querySelectorAll('.how-ladder li').forEach((li, i) => {
            const a = 0.06 + i * 0.3;
            const s = out(seg(t, a, a + 0.22));
            li.style.opacity = s;
            li.style.transform = 'translateX(' + (-18 * (1 - s)) + 'px)';
            const d = seg(t, a + 0.14, a + 0.32);
            li.querySelectorAll('.how-lmark svg > *').forEach((p) => { p.style.strokeDashoffset = String(1 - d); });
            // The open gate keeps breathing once drawn: the market is waiting on it.
            if (li.classList.contains('is-next')) li.classList.toggle('is-waiting', t >= 1);
          });
        },
        function (t) { // 3. the phone comes up, the clock lights, the alert drops in
          const ph = how.querySelector('.iph');
          if (ph) {
            ph.style.opacity = seg(t, 0, 0.25);
            ph.style.transform = 'translateY(' + (80 * (1 - out(seg(t, 0, 0.4)))) + 'px)';
          }
          how.querySelectorAll('.iph-date, .iph-time').forEach((el, i) => {
            const s = out(seg(t, 0.22 + i * 0.06, 0.48 + i * 0.06));
            el.style.opacity = s;
            el.style.transform = 'translateY(' + (10 * (1 - s)) + 'px)';
          });
          const n = how.querySelector('.iph-notif');
          if (n) {
            const s = seg(t, 0.55, 0.9);
            n.style.opacity = clamp(s * 2.5);
            n.style.transform = 'translateY(' + (-44 * (1 - back(s))) + 'px) scale(' + (0.9 + 0.1 * back(s)) + ')';
          }
        }
      ];
      const setStep = (i, t) => {
        play[i](t);
        const a = out(seg(t, 0, 0.35));
        steps[i].style.opacity = a;
        steps[i].style.transform = a >= 1 ? '' : 'translateY(' + (24 * (1 - a)) + 'px)';
        steps[i].classList.toggle('is-played', t >= 1);
      };
      steps.forEach((_, i) => setStep(i, 0));

      const run = (i) => tween(2000, (t) => setStep(i, t));
      // Three columns: the row plays in turn once it is in view. One column (≤900px, home.css
      // .how-steps): each step plays as it scrolls in, or the later ones would play
      // off-screen.
      if (window.matchMedia('(max-width: 900px)').matches) {
        steps.forEach((s, i) => onArrive(s, () => run(i), 0.35));
      } else {
        onArrive(how.querySelector('.how-steps'), () => {
          steps.forEach((_, i) => setTimeout(() => run(i), i * 1100));
        }, 0.3);
      }
    }

    // ── The engine's steps (operator, 2026-10-19) ─────────────────────────────────────
    // The same rhythm as the three above: the candles print left to right; on the same candles
    // the marks arrive - the swing labels, the change-of-character line drawn along its level,
    // the order block opening out to the right; then the reading's rows come in one by one.
    const eng = document.getElementById('engine');
    const esteps = eng ? Array.prototype.slice.call(eng.querySelectorAll('.engine-steps .how-step')) : [];
    if (esteps.length === 3) {
      esteps.forEach((s) => { s.classList.remove('reveal-up'); s.classList.add('visible'); });
      const first = esteps[0].querySelector('.eng-chart');
      const choch = eng.querySelector('.eng-choch');
      // drawn along its level: a dash as long as the line, offset from full to none (its own
      // dashes come back once it is drawn)
      const chochLen = choch ? +choch.getAttribute('x2') - +choch.getAttribute('x1') : 0;
      const ob = eng.querySelector('.eng-ob');
      const ow = ob ? +ob.getAttribute('width') : 0;
      const eplay = [
        function (t) { // 1. the candles print, left to right
          const cs = first ? first.querySelectorAll('.eng-c') : [];
          cs.forEach((c, i) => {
            const a = 0.05 + i * (0.75 / cs.length);
            const s = out(seg(t, a, a + 0.12));
            c.style.opacity = s;
            c.style.transform = 'translateY(' + (6 * (1 - s)) + 'px)';
          });
        },
        function (t) { // 2. the marks arrive on the same candles
          eng.querySelectorAll('.eng-lbl').forEach((l, i) => { l.style.opacity = out(seg(t, 0.05 + i * 0.1, 0.25 + i * 0.1)); });
          if (choch) {
            const d = out(seg(t, 0.4, 0.7));
            choch.style.opacity = d > 0 ? 1 : 0;
            choch.style.strokeDasharray = d >= 1 ? '' : (chochLen * d) + ' ' + chochLen;
          }
          const tagC = eng.querySelector('.eng-tag--choch');
          if (tagC) tagC.style.opacity = out(seg(t, 0.6, 0.78));
          if (ob) { ob.setAttribute('width', String(ow * out(seg(t, 0.62, 0.95)))); ob.style.opacity = seg(t, 0.62, 0.7); }
          const tagO = eng.querySelector('.eng-tag--ob');
          if (tagO) tagO.style.opacity = out(seg(t, 0.85, 1));
        },
        function (t) { // 3. the reading, row by row
          const sym = esteps[2].querySelector('.how-ladder-sym');
          if (sym) sym.style.opacity = out(seg(t, 0, 0.15));
          esteps[2].querySelectorAll('.eng-reading li').forEach((li, i) => {
            const a = 0.06 + i * 0.28;
            const s = out(seg(t, a, a + 0.22));
            li.style.opacity = s;
            li.style.transform = 'translateX(' + (-18 * (1 - s)) + 'px)';
          });
        }
      ];
      const setE = (i, t) => {
        eplay[i](t);
        const a = out(seg(t, 0, 0.35));
        esteps[i].style.opacity = a;
        esteps[i].style.transform = a >= 1 ? '' : 'translateY(' + (24 * (1 - a)) + 'px)';
        esteps[i].classList.toggle('is-played', t >= 1);
      };
      esteps.forEach((_, i) => setE(i, 0));
      const runE = (i) => tween(2000, (t) => setE(i, t));
      if (window.matchMedia('(max-width: 900px)').matches) {
        esteps.forEach((s, i) => onArrive(s, () => runE(i), 0.35));
      } else {
        onArrive(eng.querySelector('.engine-steps'), () => {
          esteps.forEach((_, i) => setTimeout(() => runE(i), i * 1100));
        }, 0.3);
      }
    }

    // ── The builder and the conditions list ───────────────────────────────────────────
    const demo = document.querySelector('#gates .gates-demo');
    onArrive(demo, () => demo.classList.add('is-arrived'), 0.15);
    const groups = document.querySelector('.gates-groups');
    if (groups) {
      // Group by group, each name a moment after the last (the delay is read by home.css).
      groups.querySelectorAll('.gates-group').forEach((g, gi) => {
        const dt = g.querySelector('dt');
        if (dt) dt.style.setProperty('--arrive-ms', (gi * 180) + 'ms');
        g.querySelectorAll('.gates-chips li').forEach((li, j) => li.style.setProperty('--arrive-ms', (gi * 180 + 120 + j * 60) + 'ms'));
      });
      onArrive(groups, () => groups.classList.add('is-arrived'), 0.2);
    }
  })();

  // ============================================================
  // THE FILTER WINDOW — the desk's own, both tabs, as a demonstration
  //
  // window.NtlMarketFilter and window.NtlGatePanel: the SAME two modules the Trading Desk
  // opens from its filter button, in the same window, with the same Filter / Gates switch.
  // Not a marketing copy of them — there is no marketing copy of them.
  //
  // `demo: true` on both is what makes that honest. The catalogue, the conditions, the
  // timeframe chips, AND/OR, adding and reordering gates, the warnings and the both-sides
  // notes all behave exactly as they do on the desk; every control that would need a server
  // behind it is locked at the element with a padlock on it, and carries no data-* hook for
  // the delegated handlers to find. A host in demo mode also wires no callbacks at all, so
  // there is nothing behind them either.
  //
  // NO REQUEST IS MADE FROM THIS SECTION. The panels own no URLs — every server action is a
  // host callback — so passing none is how this page says there is nothing behind it.
  // ============================================================
  (function () {
    const wrap = document.getElementById('mkt-filter');
    const filterBody = document.getElementById('mkt-filter-body');
    const gateBody = document.getElementById('mkt-gate-body');
    if (!wrap || !filterBody || !gateBody || !window.NtlMarketFilter) return;

    const NMF = window.NtlMarketFilter;

    // The desk's mode switch, minus its plumbing. On the desk setMode also runs
    // activate/deactivate hooks (they start and stop the FilterGrid polling for whichever
    // panel is in front) and persists the choice. Neither applies here: nothing polls, and
    // there is no next visit to remember.
    const opts = wrap.querySelectorAll('.mktm-mode [data-mode]');
    function setMode(next) {
      for (let i = 0; i < opts.length; i++) {
        const on = opts[i].getAttribute('data-mode') === next;
        opts[i].classList.toggle('is-sel', on);
        opts[i].setAttribute('aria-checked', on ? 'true' : 'false');
      }
      filterBody.hidden = next !== 'filter';
      gateBody.hidden = next !== 'gates';
    }
    for (let i = 0; i < opts.length; i++) {
      opts[i].addEventListener('click', function () { setMode(this.getAttribute('data-mode')); });
    }

    // EVERY CONDITION IS OPEN here. A version locked most of them — padlocked, then blurred —
    // behind an upgrade link; it went on the operator's call (2026-09-22) as more tease than
    // honest demo. What a visitor cannot do from this page is SAVE or ALERT, and the builder
    // says so itself; the prompt to join sits under the window (.gates-cta, Index.cshtml).

    // Both mounted eagerly. Neither needs to be in front to build itself, and a panel that
    // only appears once you have clicked its tab is a panel most visitors never see.
    // joinHref: "Join NTL to save" takes the same route as every CTA on this page —
    // PaymentController.Start: Stripe Checkout when signed in, else sign-in then the
    // purchase page.
    const JOIN_HREF = '/Account/Payment/Start';
    NMF.mount({ container: filterBody, demo: true, timeframes: NMF.TFS, joinHref: JOIN_HREF });

    if (window.NtlGatePanel) {
      // The seed is taken first so the header can name it (exampleName, 2026-10-15): the demo's
      // header shows the example's name where the desk shows the system being edited.
      const seed = (window.NtlGatePanel.starters() || [])[0];
      const gates = window.NtlGatePanel.mount({ container: gateBody, demo: true, timeframes: NMF.TFS, joinHref: JOIN_HREF, exampleName: seed ? seed.name : '' });

      // Opened on a ladder rather than on an empty builder. An empty panel shows a blank
      // gate and asks the visitor to invent a strategy before they have seen one work; this
      // opens on the thing the table above just demonstrated.
      //
      // From starters() — the panel's OWN built-in examples, the same list its "Start from an
      // example" buttons offer — so the seed is built from the live catalogue and cannot fall
      // out of step with a condition whose options change in C#. It is also exactly what the
      // panel does to itself when one of those buttons is pressed.
      if (seed) gates.setSpec(seed.spec, 0);
    }
  })();

  // ============================================================
  // REEL MODE — the hero as a vertical (9:16) video, for screen-recording an Instagram Reel
  //
  // Not for visitors: nothing on the page links to it. Either load the page with ?reel
  // (?reel=4 for a speed other than 6x) or run ntlReel() — ntlReel({ speed: 4 }) — in the
  // console. Both do the same thing:
  //   - html.is-reel (home.css) takes off the navbar, every section but the hero and the
  //     footer, and fills the screen with the hero;
  //   - the headline, sub-line, table and actions come in one after another, afresh;
  //   - the gate ladder replays from gate 1 at `speed` times its normal pace.
  // Calling it again replays the whole sequence, so every take starts from the same frame.
  // ============================================================
  (function () {
    const hero = document.getElementById('hero');
    if (!hero) return;

    // The sub-line is two sentences split by a <br>; each becomes a line of its own so they
    // can arrive one at a time. Done once — a second call finds the spans already there.
    function splitSub() {
      const sub = hero.querySelector('.hero-sub');
      if (!sub || sub.querySelector('.reel-line')) return;
      const lines = [];
      sub.childNodes.forEach(function (n) {
        if (n.nodeType === 3 && n.textContent.trim()) lines.push(n.textContent.trim());
      });
      if (!lines.length) return;
      sub.textContent = '';
      lines.forEach(function (t) {
        const s = document.createElement('span');
        s.className = 'reel-line';
        s.textContent = t;
        sub.appendChild(s);
      });
    }

    // Restart a CSS entrance on `el` after `delay` ms. The class comes off and the reflow in
    // between is what makes the browser run the animation again rather than ignore it.
    function enter(el, delay) {
      if (!el) return;
      el.classList.remove('reveal-up', 'reel-in');
      el.classList.add('visible');
      el.style.setProperty('--reel-d', delay + 'ms');
      void el.offsetWidth;
      el.classList.add('reel-in');
    }

    // The logo, centred over the headline and on screen for the whole reel (no entrance): the
    // navbar that normally carries it is gone. The navbar's own two images, so it follows the
    // theme exactly as the navbar does (navbar.css shows one and hides the other).
    function addLogo() {
      const grid = hero.querySelector('.hero-grid');
      if (!grid || grid.querySelector('.reel-logo')) return;
      const box = document.createElement('div');
      box.className = 'reel-logo';
      document.querySelectorAll('#nav-logo .nav-logo-img').forEach(function (img) {
        box.appendChild(img.cloneNode(true));
      });
      grid.insertBefore(box, grid.firstChild);
    }

    window.ntlReel = function (opts) {
      const speed = Math.max(0.25, Number(opts && opts.speed) || 6);
      document.documentElement.classList.add('is-reel');
      window.scrollTo(0, 0);
      splitSub();
      addLogo();

      // TOP TO BOTTOM, ONE THING PER BEAT, so the eye drifts down the frame with it and never
      // has two things to read at once:
      //   headline ×2 → sub-line ×2 → (two beats) → the table → gates 1–4 → the alert →
      //   (two beats) → the actions and the price, together
      // Each is one BEAT after the last, except the table and the actions (two) and the GATES,
      // which change every two beats (operator, 2026-09-27: half the pace the text comes in
      // at), with the alert one gate-step after the last. `speed` paces the whole reel.
      const keeps = hero.querySelectorAll('.hero-title .hero-keep');
      const subs = hero.querySelectorAll('.hero-sub .reel-line');
      hero.querySelectorAll('.hero-title, .hero-sub').forEach(function (el) {
        el.classList.remove('reveal-up');
        el.classList.add('visible');
      });
      // START: a moment after load, so the first line is not fighting the page (fonts, the
      // background canvas) for its opening frames.
      const START = 500;
      // 3000 / 6 = 500ms at the default: a clean 120 BPM, so every entrance lands on a half-second
      // grid (and on the beat of most music a Reel is cut to) rather than on 533ms steps.
      const BEAT = Math.round(3000 / speed);
      const lines = [keeps[0], keeps[1], subs[0], subs[1]];
      lines.forEach(function (el, i) { enter(el, START + i * BEAT); });
      // A DOUBLE beat before the table, so the sub-line is read before the table starts moving.
      const STAGE_AT = START + (lines.length + 1) * BEAT;
      enter(hero.querySelector('.hero-stage'), STAGE_AT);
      const GATE1_AT = STAGE_AT + BEAT;

      // The ladder takes the next five gate-steps (four gates, then the alert). The actions and the
      // price line arrive TOGETHER, and after a DOUBLE beat — a pause, so the alert is read
      // before the ask. enter() holds each hidden until its beat.
      const GATE_STEP = 2 * BEAT;
      const toAlert = reelHooks.ladder ? reelHooks.ladder(GATE_STEP, GATE1_AT) : GATE1_AT;
      enter(hero.querySelector('.hero-actions'), toAlert + 2 * BEAT);
      enter(hero.querySelector('.hero-note'), toAlert + 2 * BEAT);

      return 'Reel mode on at ' + speed + 'x. Run ntlReel() again to replay from the start.';
    };

    const q = new URLSearchParams(window.location.search);
    if (q.has('reel')) window.ntlReel({ speed: Number(q.get('reel')) || 6 });
  })();

});
